import { useState, useCallback, useEffect } from 'react';

export interface HistoryEntry {
  id: string;
  type: string;
  title: string;
  summary: string;
  timestamp: Date;
  data: Record<string, unknown>;
}

const HISTORY_STORAGE_KEY = 'calculatorsheet_global_history';
const MAX_HISTORY_ENTRIES = 100;

// Parse dates when loading from localStorage
const parseHistoryEntry = (entry: HistoryEntry): HistoryEntry => ({
  ...entry,
  timestamp: new Date(entry.timestamp),
});

// Load history from localStorage
const loadHistory = (): HistoryEntry[] => {
  try {
    const stored = localStorage.getItem(HISTORY_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return parsed.map(parseHistoryEntry);
    }
  } catch (e) {
    console.error('Failed to load history:', e);
  }
  return [];
};

// Save history to localStorage
const saveHistory = (history: HistoryEntry[]) => {
  try {
    localStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(history));
  } catch (e) {
    console.error('Failed to save history:', e);
  }
};

export function useGlobalHistory() {
  const [history, setHistory] = useState<HistoryEntry[]>(() => loadHistory());

  // Persist history whenever it changes
  useEffect(() => {
    saveHistory(history);
  }, [history]);

  const addToHistory = useCallback((entry: Omit<HistoryEntry, 'id' | 'timestamp'>) => {
    const newEntry: HistoryEntry = {
      ...entry,
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date(),
    };

    setHistory(prev => {
      // Check for duplicate (same type, title, summary within 2 seconds)
      const isDuplicate = prev.some(
        e => 
          e.type === newEntry.type && 
          e.title === newEntry.title && 
          e.summary === newEntry.summary &&
          Math.abs(new Date(e.timestamp).getTime() - newEntry.timestamp.getTime()) < 2000
      );

      if (isDuplicate) {
        return prev;
      }

      return [newEntry, ...prev].slice(0, MAX_HISTORY_ENTRIES);
    });

    return newEntry.id;
  }, []);

  const deleteEntry = useCallback((id: string) => {
    setHistory(prev => prev.filter(e => e.id !== id));
  }, []);

  const clearHistory = useCallback(() => {
    setHistory([]);
  }, []);

  const clearHistoryByType = useCallback((type: string) => {
    setHistory(prev => prev.filter(e => e.type !== type));
  }, []);

  return {
    history,
    addToHistory,
    deleteEntry,
    clearHistory,
    clearHistoryByType,
  };
}
